import { useEffect, useLayoutEffect } from "react";
import { Unloaded, useClient, useConnection, useRefetch, useSession } from "../../client/index.ts";
import type { ClientError, Opened, PlaceFacts } from "../../client/index.ts";
import { Button } from "../../design/primitives/Button.tsx";
import { Page } from "../../design/primitives/Page.tsx";
import { Skeleton } from "../../design/primitives/Skeleton.tsx";
import { Banner } from "../../design/primitives/Banner.tsx";
import { shellCopy } from "../copy.ts";
import { ErrorBoundary } from "../errors/ErrorBoundary.tsx";
import { goneWords } from "../errors/words.ts";
import { Breadcrumbs } from "../navigation/Breadcrumbs.tsx";
import { Siblings } from "../navigation/Siblings.tsx";
import { OpeningPage } from "../navigation/Link.tsx";
import { useNavigation } from "../navigation/Navigation.tsx";
import styles from "./Frame.module.css";
import header from "../navigation/Header.module.css";
import { designCopy } from "../../design/copy.ts";
import { hold, keep, type Held, type Screen } from "./steady.ts";
import { classes, quietLink } from "../../design/classes.ts";

export function Frame() {
  const { levels, route } = useNavigation();
  useSteadyScreen();
  return (
    <div data-palco="">
      {levels.map((level) => (
        <div key={level.id} data-nivel={level.id} inert={level.route !== route}>
          {level.route.opened ? (
            <OpeningPage value={level.id === 0}>
              <Place opened={level.route.opened} url={level.route.url} here={level.route === route} />
            </OpeningPage>
          ) : <NotFound />}
        </div>
      ))}
    </div>
  );
}

type Interaction =
  | { readonly kind: "none" | "keyboard" }
  | { readonly kind: "pointer"; readonly x: number; readonly y: number };

function useSteadyScreen(): void {
  useEffect(() => {
    let interaction: Interaction = { kind: "none" };
    const screen = interactionScreen(() => interaction);
    const scroller = document.documentElement.style;
    const anchoring = scroller.overflowAnchor;
    let held: Held<Element> | null = null;
    const again = () => {
      held = hold(screen);
      const wanted = held === null ? anchoring : "none";
      if (scroller.overflowAnchor !== wanted) scroller.overflowAnchor = wanted;
    };
    again();
    const pointed = (event: PointerEvent) => {
      interaction = event.pointerType === "touch" ? { kind: "none" } : { kind: "pointer", x: event.clientX, y: event.clientY };
      again();
    };
    const keyed = () => {
      interaction = { kind: "keyboard" };
      again();
    };
    const left = (event: PointerEvent) => {
      if (event.relatedTarget !== null || interaction.kind !== "pointer") return;
      interaction = { kind: "none" };
      again();
    };
    const redrawn = new MutationObserver(() => {
      if (held) keep(screen, held);
      again();
    });
    redrawn.observe(document.body, { subtree: true, childList: true, characterData: true, attributeFilter: ["class"] });
    document.addEventListener("pointermove", pointed, { passive: true });
    document.addEventListener("pointerdown", pointed, { passive: true });
    document.addEventListener("pointerout", left);
    document.addEventListener("keydown", keyed, true);
    document.addEventListener("focusin", again);
    addEventListener("scroll", again, { passive: true });
    return () => {
      redrawn.disconnect();
      scroller.overflowAnchor = anchoring;
      document.removeEventListener("pointermove", pointed);
      document.removeEventListener("pointerdown", pointed);
      document.removeEventListener("pointerout", left);
      document.removeEventListener("keydown", keyed, true);
      document.removeEventListener("focusin", again);
      removeEventListener("scroll", again);
    };
  }, []);
}

function interactionScreen(interaction: () => Interaction): Screen<Element> {
  return {
    pointed: () => {
      const input = interaction();
      const element = input.kind === "pointer" ? document.elementFromPoint(input.x, input.y)
        : input.kind === "keyboard" ? document.activeElement : null;
      if (input.kind === "keyboard" && element !== null) {
        const rect = element.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= innerHeight) return null;
      }
      const level = element?.closest("[data-nivel]:not([inert]):not([data-janela])");
      if (!element || !level) return null;
      const control = element.closest("a, button, input, textarea, select, [tabindex]");
      return control && level.contains(control) ? control : element.closest("svg") ?? element;
    },
    top: (element) => element.getBoundingClientRect().top,
    connected: (element) => element.isConnected,
    where: () => `${location.href} ${scrollX},${scrollY} ${innerWidth}x${innerHeight}`,
    scrollBy: (pixels) => scrollBy(0, pixels),
  };
}

function Place({ opened, url, here }: {
  readonly opened: Opened;
  readonly url: string;
  readonly here: boolean;
}) {
  const client = useClient();
  const rendered = opened.use();
  const refetch = useRefetch(opened.key);
  const facts = rendered.status === "pronta" ? rendered.facts : undefined;
  useTitle(facts, here);
  useNeighbours(here ? facts : undefined);
  const { pageIn } = useNavigation();
  const ready = here && rendered.status === "pronta";
  useLayoutEffect(() => {
    if (ready) pageIn();
  }, [ready, pageIn]);
  if (rendered.status === "erro") return <Failed error={rendered.state.error} retry={refetch} {...(opened.missing === undefined ? {} : { missing: opened.missing })} />;
  if (rendered.status === "falhou") return <Page><Unloaded /></Page>;
  if (rendered.status === "carregando") {
    const Loading = rendered.Loading;
    return <Page>{Loading ? <Loading /> : <Placeholder />}</Page>;
  }
  return (
    <Page>
      {facts ? <HeaderRow facts={facts} /> : null}
      {rendered.error && rendered.error.kind !== "rede" ? (
        <div className={styles.stale}>
          <Banner tone="negativo" action={<Button onClick={refetch}>{designCopy.tentarDeNovo}</Button>}>
            {shellCopy.tela.naoCarregou}
          </Banner>
        </div>
      ) : null}
      <ErrorBoundary view={url} report={client.reportError}>
        {rendered.element}
      </ErrorBoundary>
    </Page>
  );
}

function HeaderRow({ facts }: { readonly facts: PlaceFacts }) {
  const session = useSession();
  const accounts = session.status === "dentro" ? session.view.derived.accounts.length : 0;
  const path = accounts >= 2 ? facts.path : facts.path.filter((ref) => ref.place !== "conta");
  if (path.length === 0 && facts.siblings === undefined) return null;
  return (
    <div className={header.row}>
      <Breadcrumbs path={path} title={facts.title} />
      <div className={header.end}>
        {facts.siblings && facts.siblings.count > 1 ? <Siblings siblings={facts.siblings} /> : null}
        {facts.managerUrl ? (
          <a className={classes(header.manager, quietLink)} href={facts.managerUrl} target="_blank" rel="noreferrer">
            {designCopy.abrirNoGerenciador}
          </a>
        ) : null}
      </div>
    </div>
  );
}

function useNeighbours(facts: PlaceFacts | undefined): void {
  const { prefetch } = useNavigation();
  const hrefs = [facts?.siblings?.prev?.href, facts?.siblings?.next?.href, facts?.parent ?? facts?.path.at(-1)?.href].filter((href): href is string => href !== undefined).join(" ");
  useEffect(() => {
    if (hrefs === "") return;
    const timer = setTimeout(() => hrefs.split(" ").forEach(prefetch), 300);
    return () => clearTimeout(timer);
  }, [hrefs, prefetch]);
}

function useTitle(facts: PlaceFacts | undefined, here: boolean): void {
  const session = useSession();
  const brand = session.status === "dentro" ? session.view.ui.brand.name : undefined;
  useEffect(() => {
    if (!here) return;
    const parts = [facts?.title, brand].filter((part): part is string => part !== undefined);
    document.title = parts.length > 0 ? parts.join(" · ") : shellCopy.nabla;
  }, [facts?.title, brand, here]);
}

function Placeholder() {
  return (
    <div className={styles.placeholder} aria-busy="true">
      <Skeleton width="38%" height="14px" />
      <Skeleton width="62%" height="34px" />
      <Skeleton lines={2} width="70%" />
      <Skeleton height="140px" />
      <Skeleton height="220px" />
    </div>
  );
}

function Failed({ error, retry, missing }: { readonly error: ClientError; readonly retry: () => void; readonly missing?: string }) {
  const { offline } = useConnection();
  const gone = goneWords(error, missing);
  if (gone !== undefined) return <NotFound text={gone} />;
  if (error.kind === "rede" && offline) return <Page><Placeholder /></Page>;
  return (
    <Page>
      <Banner tone="negativo" action={<Button onClick={retry}>{designCopy.tentarDeNovo}</Button>}>
        {shellCopy.tela.naoCarregou}
      </Banner>
    </Page>
  );
}

function NotFound({ text = designCopy.erros["nao-encontrado"] }: { readonly text?: string }) {
  return (
    <Page>
      <p className={styles.notFound}>{text}</p>
      <div className={styles.actions}>
        <Button href="/" variant="primario">
          {shellCopy.tela.voltarParaVisaoGeral}
        </Button>
      </div>
    </Page>
  );
}
